import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { LanguageContext } from "../../context/LanguageContext";

const GITHUB_USER = "JacquesGomes";
const GITHUB_URL = "https://github.com/JacquesGomes";
const CONTRIBUTIONS_URL = `https://github-contributions-api.jogruber.de/v4/${GITHUB_USER}`;

const FALLBACK = {
  total: { 2022: 27, 2023: 1956, 2024: 1317, 2025: 844, 2026: 1900 },
  contributions: [],
};

const STACK = ["Python", "FastAPI", "GCP", "Docker", "React", "TypeScript", "Qdrant", "LangChain"];

const labels = {
  en: {
    title: "GitHub",
    subtitle: "Most of the production work lives in private repos. The graph still tells the pace.",
    viewProfile: "Open profile",
    contributions: "Contributions",
    years: "Active years",
    repos: "Public repos",
    longest: "Longest streak",
    stack: "What I actually use",
    lastYear: "Last twelve months",
  },
  pt: {
    title: "GitHub",
    subtitle: "O trabalho em produção mora em repositórios privados. O gráfico ainda mostra o ritmo.",
    viewProfile: "Abrir perfil",
    contributions: "Contribuições",
    years: "Anos ativos",
    repos: "Repos públicos",
    longest: "Maior sequência",
    stack: "O que eu uso de verdade",
    lastYear: "Últimos doze meses",
  },
};

const levelColor = (level) => {
  const scale = [
    "rgba(243,239,230,0.06)",
    "rgba(198,164,106,0.28)",
    "rgba(198,164,106,0.5)",
    "rgba(198,164,106,0.72)",
    "#c6a46a",
  ];
  return scale[Math.max(0, Math.min(level, 4))];
};

const computeStats = (payload) => {
  const totals = payload.total || {};
  const all = Object.values(totals).reduce((sum, n) => sum + Number(n || 0), 0);
  const years = Object.keys(totals).filter((y) => Number(totals[y]) > 0).length;
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const days = [...(payload.contributions || [])]
    .filter((day) => new Date(`${day.date}T00:00:00`) <= today)
    .sort((a, b) => a.date.localeCompare(b.date));

  let longest = 0;
  let current = 0;
  days.forEach((day) => {
    if (day.count > 0) {
      current += 1;
      longest = Math.max(longest, current);
    } else {
      current = 0;
    }
  });

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 364);
  const recent = days.filter((d) => new Date(`${d.date}T00:00:00`) >= cutoff);

  return { all, years, longest, recent };
};

const Stat = ({ value, label }) => (
  <Box>
    <Typography
      component="p"
      sx={{
        fontSize: { xs: "1.6rem", md: "1.85rem" },
        fontWeight: 700,
        letterSpacing: -0.6,
        color: "#f3efe6",
        lineHeight: 1.1,
        mb: 0.6,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", letterSpacing: 0.4, display: "block" }}
    >
      {label}
    </Typography>
  </Box>
);

const GitHubStats = () => {
  const { language } = useContext(LanguageContext);
  const t = labels[language] || labels.en;
  const [payload, setPayload] = useState(FALLBACK);

  useEffect(() => {
    let alive = true;
    fetch(CONTRIBUTIONS_URL)
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        if (alive && data?.total) setPayload(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const stats = useMemo(() => computeStats(payload), [payload]);
  const weeks = Math.max(1, Math.ceil(stats.recent.length / 7));

  return (
    <Box
      component="section"
      aria-labelledby="github-heading"
      sx={{ borderTop: "1px solid", borderColor: "divider", py: { xs: 8, md: 10 } }}
    >
      <Box className="page-width">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
          mb={4}
        >
          <Box sx={{ maxWidth: 560 }}>
            <Typography
              id="github-heading"
              variant="h2"
              component="h2"
              sx={{ textAlign: "left", textTransform: "none", letterSpacing: -0.6, mb: 1 }}
            >
              {t.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {t.subtitle}
            </Typography>
          </Box>
          <Box
            component="a"
            href={GITHUB_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              flexShrink: 0,
              mt: 0.6,
              color: "#c6a46a",
              fontSize: "0.9rem",
              textDecoration: "none",
              borderBottom: "1px solid transparent",
              "&:hover": { borderBottomColor: "#c6a46a" },
            }}
          >
            {t.viewProfile} →
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: { xs: 3, md: 4 },
            mb: 5,
            py: 0.5,
          }}
        >
          <Stat value={stats.all.toLocaleString(language === "pt" ? "pt-BR" : "en-US")} label={t.contributions} />
          <Stat value={stats.years || 4} label={t.years} />
          <Stat value={21} label={t.repos} />
          <Stat value={stats.longest || 16} label={t.longest} />
        </Box>

        {stats.recent.length > 0 && (
          <Box mb={5}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                letterSpacing: 0.4,
                display: "block",
                mb: 1.25,
              }}
            >
              {t.lastYear}
            </Typography>
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                overflowX: "auto",
                overflowY: "hidden",
                overscrollBehaviorX: "contain",
                WebkitOverflowScrolling: "touch",
                pb: 0.5,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${weeks}, minmax(11px, 1fr))`,
                  gridTemplateRows: "repeat(7, 11px)",
                  gridAutoFlow: "column",
                  gap: "3px",
                  minWidth: Math.min(weeks * 14, 720),
                  width: "100%",
                }}
              >
                {stats.recent.map((day) => (
                  <Box
                    key={day.date}
                    title={`${day.date}: ${day.count}`}
                    sx={{
                      width: "100%",
                      height: 11,
                      borderRadius: "2px",
                      bgcolor: levelColor(day.level ?? (day.count === 0 ? 0 : Math.min(4, Math.ceil(day.count / 4)))),
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              letterSpacing: 0.4,
              display: "block",
              mb: 1.25,
            }}
          >
            {t.stack}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {STACK.map((item) => (
              <Box
                key={item}
                sx={{
                  px: 1.3,
                  py: 0.55,
                  borderRadius: 1.2,
                  border: "1px solid rgba(243,239,230,0.1)",
                  color: "rgba(243,239,230,0.78)",
                  fontSize: "0.8rem",
                  lineHeight: 1.4,
                }}
              >
                {item}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GitHubStats;
